/**
 * Shop page filter bar — AJAX-refreshes the product grid in place via the
 * craftis_shop_filter admin-ajax action (see craftis_ajax_shop_filter() in
 * inc/woocommerce-hooks.php) instead of a full page reload, and pushes
 * the resulting query string via history.pushState so the filtered view
 * stays a shareable/bookmarkable URL despite never actually navigating.
 *
 * The filter state spans two separate DOM locations that together act as
 * one logical filter: category pills render in the page-title banner
 * (template-parts/page-title-banner.php, only on Shop/category archives —
 * see craftis_the_shop_category_pills()), while price range, sort-by, and
 * the nonce field live in [data-craftis-shop-filters] above the grid
 * (craftis_the_shop_filter_bar()). Category pills are therefore queried
 * from `document`, not scoped to the filter bar element.
 */

// Guards against an out-of-order AJAX response: if a user fires two
// filter requests close together (two quick pill clicks, or a pill click
// right after the sort-by change fires), a slower earlier request can
// resolve AFTER a newer one and overwrite the grid with stale results —
// e.g. clicking "Accessories" right after an "All" request was still in
// flight, with "All" winning the race and landing last even though
// "Accessories" is the pill left showing active. Each call gets an
// incrementing token; only the response matching the most recently
// issued request is ever applied to the DOM.
let craftisShopFilterRequestId = 0;

function craftisReadShopFilterState( bar ) {
	const activePill = document.querySelector( '[data-craftis-filter-cat].is-active' );
	const minInput = bar.querySelector( '[data-craftis-filter-min-price]' );
	const maxInput = bar.querySelector( '[data-craftis-filter-max-price]' );
	const sortSelect = bar.querySelector( '[data-craftis-filter-orderby]' );

	return {
		product_cat: activePill ? activePill.dataset.craftisFilterCat : '',
		min_price: minInput ? minInput.value.trim() : '',
		max_price: maxInput ? maxInput.value.trim() : '',
		orderby: sortSelect && sortSelect.value !== 'menu_order' ? sortSelect.value : '',
	};
}

function craftisRunShopFilter( bar, gridWrap ) {
	const nonceInput = bar.querySelector( 'input[name="craftis_shop_filter_nonce"]' );
	if ( ! nonceInput ) {
		return;
	}

	const spinner = bar.querySelector( '[data-craftis-filter-spinner]' );
	const state = craftisReadShopFilterState( bar );

	const formData = new FormData();
	formData.append( 'action', 'craftis_shop_filter' );
	formData.append( 'craftis_shop_filter_nonce', nonceInput.value );
	Object.keys( state ).forEach( ( key ) => {
		if ( state[ key ] ) {
			formData.append( key, state[ key ] );
		}
	} );

	if ( spinner ) {
		spinner.classList.add( 'is-active' );
	}
	gridWrap.setAttribute( 'aria-busy', 'true' );

	const requestId = ++craftisShopFilterRequestId;

	fetch( craftisShopL10n.ajaxUrl, {
		method: 'POST',
		body: formData,
		credentials: 'same-origin',
	} )
		.then( ( response ) => response.json() )
		.then( ( result ) => {
			// A newer request has been issued since this one went out —
			// applying this (now-stale) response would overwrite whatever
			// the more recent request already rendered (or will render),
			// showing results that don't match the currently-active pill.
			if ( requestId !== craftisShopFilterRequestId ) {
				return;
			}

			if ( ! result.success ) {
				return;
			}

			gridWrap.innerHTML = result.data.html;

			// The page-title banner's <h1> reflects the URL at page load
			// (e.g. "Shop" or a category name from the queried object) and
			// has no way to know a category was since picked via the
			// filter pills — the AJAX response carries the heading that
			// actually matches the current selection instead.
			const heading = document.querySelector( '.craftis-page-title__heading' );
			if ( heading && result.data.heading ) {
				heading.textContent = result.data.heading;
			}

			// Same reasoning for the "Shop All" heading + result count
			// above the grid (craftis_the_shop_filter_bar()) — neither is
			// part of the swapped grid HTML, so both need updating here too.
			const shopAllTitle = document.querySelector( '.craftis-shop-filters__title' );
			if ( shopAllTitle && result.data.heading ) {
				shopAllTitle.textContent = result.data.heading;
			}

			const resultCount = document.querySelector( '.craftis-shop-filters__count' );
			if ( resultCount && result.data.resultCount ) {
				resultCount.textContent = result.data.resultCount;
			}

			const params = new URLSearchParams( result.data.queryArgs );
			const newUrl = params.toString() ? `${ window.location.pathname }?${ params.toString() }` : window.location.pathname;
			window.history.pushState( { craftisShopFilter: true }, '', newUrl );
		} )
		.catch( () => {
			// Network/parse failure: leave the current grid as-is rather
			// than clearing it, so a flaky connection doesn't blank the page.
		} )
		.finally( () => {
			if ( spinner ) {
				spinner.classList.remove( 'is-active' );
			}
			gridWrap.removeAttribute( 'aria-busy' );
		} );
}

function initShopFilters() {
	const bar = document.querySelector( '[data-craftis-shop-filters]' );
	const gridWrap = document.querySelector( '[data-craftis-shop-grid]' );
	const pillsContainer = document.querySelector( '[data-craftis-shop-category-pills]' );

	if ( ! bar || ! gridWrap ) {
		return;
	}

	// Category pills live in the page-title banner, outside `bar` — bound
	// on their own container (falls back to `bar` if pills happen to be
	// absent, e.g. a catalogue with zero product categories).
	( pillsContainer || bar ).addEventListener( 'click', ( event ) => {
		const pill = event.target.closest( '[data-craftis-filter-cat]' );
		if ( ! pill ) {
			return;
		}

		document.querySelectorAll( '[data-craftis-filter-cat]' ).forEach( ( el ) => el.classList.remove( 'is-active' ) );
		pill.classList.add( 'is-active' );
		craftisRunShopFilter( bar, gridWrap );
	} );

	bar.addEventListener( 'click', ( event ) => {
		if ( event.target.closest( '[data-craftis-filter-apply-price]' ) ) {
			craftisRunShopFilter( bar, gridWrap );
			return;
		}

		if ( event.target.closest( '[data-craftis-filter-clear]' ) ) {
			document.querySelectorAll( '[data-craftis-filter-cat]' ).forEach( ( el ) => el.classList.remove( 'is-active' ) );
			const allPill = document.querySelector( '[data-craftis-filter-cat=""]' );
			if ( allPill ) {
				allPill.classList.add( 'is-active' );
			}
			bar.querySelectorAll( '.craftis-shop-filters__price-input' ).forEach( ( input ) => {
				input.value = '';
			} );
			const sortSelect = bar.querySelector( '[data-craftis-filter-orderby]' );
			if ( sortSelect ) {
				sortSelect.value = 'menu_order';
			}
			craftisRunShopFilter( bar, gridWrap );
		}
	} );

	// Enter key inside a price input applies it too, not just the button.
	bar.addEventListener( 'keydown', ( event ) => {
		if ( event.key === 'Enter' && event.target.matches( '.craftis-shop-filters__price-input' ) ) {
			event.preventDefault();
			craftisRunShopFilter( bar, gridWrap );
		}
	} );

	const sortSelect = bar.querySelector( '[data-craftis-filter-orderby]' );
	if ( sortSelect ) {
		sortSelect.addEventListener( 'change', () => craftisRunShopFilter( bar, gridWrap ) );
	}

	initShopAddedToCartNotice( bar );
}

/**
 * "N items in cart — View Cart" notice, shown in the filter bar after an
 * AJAX add-to-cart from the grid below. WooCommerce's add-to-cart.js
 * fires "added_to_cart" on jQuery(document.body) with the fragments
 * object as its first argument — the same event the header's mini-cart
 * dropdown already listens for (see initCartDropdown() in header.js) — so
 * this reuses it rather than adding a second AJAX round-trip just to
 * learn the new cart count.
 *
 * @param {HTMLElement} bar The [data-craftis-shop-filters] bar.
 */
function initShopAddedToCartNotice( bar ) {
	if ( ! window.jQuery ) {
		return;
	}

	const notice = bar.querySelector( '[data-craftis-added-notice]' );
	const textEl = bar.querySelector( '[data-craftis-added-text]' );
	if ( ! notice || ! textEl ) {
		return;
	}

	window.jQuery( document.body ).on( 'added_to_cart', ( event, fragments ) => {
		const countHtml = fragments && fragments[ '.craftis-header__cart-count' ];
		const countMatch = countHtml && countHtml.match( />(\d+)</ );
		const count = countMatch ? parseInt( countMatch[ 1 ], 10 ) : null;

		textEl.textContent = null === count
			? craftisShopL10n.addedGeneric
			: count === 1
				? craftisShopL10n.addedSingular
				: craftisShopL10n.addedPlural.replace( '%d', count );

		notice.hidden = false;

		bar.scrollIntoView( { behavior: 'smooth', block: 'start' } );
	} );
}

document.addEventListener( 'DOMContentLoaded', initShopFilters );
